import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Wallet,
  Send,
  DollarSign,
  CreditCard,
  RotateCcw,
  Calendar,
  Clock,
  TrendingUp,
  Award,
  CheckCircle,
  Camera,
  Image,
  X,
  Plus,
} from "lucide-react";

export default function VendedorCierre() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [ventasHoy, setVentasHoy] = useState(null);
  const [comisionesInfo, setComisionesInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cierreExitoso, setCierreExitoso] = useState(false);
  const [comisionCalculada, setComisionCalculada] = useState(null);
  const [fotosMermas, setFotosMermas] = useState([]);
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const [showResumen, setShowResumen] = useState(false);
  const [form, setForm] = useState({
    total_efectivo: "",
    total_transferencia: "",
    unidades_devueltas: "0",
    notas: "",
  });

  const fechaActual = new Date();
  const fechaFormateada = fechaActual.toLocaleDateString("es-CO", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const horaFormateada = fechaActual.toLocaleTimeString("es-CO", {
    hour: "2-digit",
    minute: "2-digit",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ventasRes, comisionesRes] = await Promise.all([
        api.get("/ventas/hoy"),
        api.get("/comisiones/calcular"),
      ]);
      setVentasHoy(ventasRes.data);
      setComisionesInfo(comisionesRes.data);

      const efectivo =
        ventasRes.data.ventas
          ?.filter((v) => v.metodo_pago === "efectivo")
          .reduce((sum, v) => sum + v.total, 0) || 0;
      const transferencia =
        ventasRes.data.ventas
          ?.filter((v) => v.metodo_pago === "transferencia")
          .reduce((sum, v) => sum + v.total, 0) || 0;

      setForm({
        ...form,
        total_efectivo: efectivo.toString(),
        total_transferencia: transferencia.toString(),
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploadingFoto(true);
    for (const file of files) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} no es una imagen válida`);
        continue;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} es demasiado grande. Máximo 5MB`);
        continue;
      }

      // Convert to base64
      const reader = new FileReader();
      reader.onload = (e) => {
        setFotosMermas((prev) => [...prev, { data: e.target.result, name: file.name }]);
      };
      reader.readAsDataURL(file);
    }
    setUploadingFoto(false);
    event.target.value = "";
  };

  const handleRemoveFoto = (index) => {
    setFotosMermas((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!form.total_efectivo && !form.total_transferencia) {
      toast.error("Debes ingresar al menos el total en efectivo o transferencias");
      return;
    }
    
    if (form.total_efectivo === "" && form.total_transferencia === "") {
      toast.error("Los campos de efectivo y transferencia son obligatorios");
      return;
    }
    
    // Show preview first
    if (!showResumen) {
      setShowResumen(true);
      return;
    }
    
    setSubmitting(true);

    try {
      const payload = {
        total_efectivo: parseFloat(form.total_efectivo) || 0,
        total_transferencia: parseFloat(form.total_transferencia) || 0,
        unidades_devueltas: parseInt(form.unidades_devueltas) || 0,
        notas: form.notas || null,
        fotos_mermas: fotosMermas.map((f) => f.data),
      };

      await api.post("/cierres", payload);

      // Get commission from last cierre
      const cierresRes = await api.get("/cierres");
      const ultimoCierre = cierresRes.data[0];
      if (ultimoCierre?.comision_calculada) {
        setComisionCalculada(ultimoCierre.comision_calculada);
      }

      setCierreExitoso(true);
      setShowResumen(false);
      toast.success("¡Cierre enviado correctamente!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar cierre");
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  const totalReportado =
    (parseFloat(form.total_efectivo) || 0) + (parseFloat(form.total_transferencia) || 0);
  const diferencia = totalReportado - (ventasHoy?.total || 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-torta-brown">Cargando...</div>
      </div>
    );
  }

  // Vista de cierre exitoso con comisiones
  if (cierreExitoso) {
    return (
      <div className="space-y-6" data-testid="vendedor-cierre-exitoso">
        <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="font-display text-3xl text-green-800 mb-2">¡Cierre Enviado!</h1>
          <p className="text-green-600">Tu cierre de caja ha sido registrado correctamente</p>
        </div>

        {comisionCalculada && (
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <Award className="w-8 h-8 text-amber-500" />
              <h2 className="font-display text-2xl text-torta-dark">Tu Comisión del Día</h2>
            </div>

            <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-6 text-white mb-6">
              <p className="text-white/80 text-sm mb-1">Total Comisión</p>
              <p className="font-display text-4xl">
                {formatCurrency(comisionCalculada.total_comision)}
              </p>
            </div>

            {/* Mensaje si no hay comisión configurada */}
            {comisionCalculada.total_comision === 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                <p className="text-amber-800 text-sm">
                  <strong>Nota:</strong> Tu comisión aparece en $0 porque las comisiones de productos aún no han sido configuradas por el administrador. 
                  Contacta a tu supervisor para que configure las comisiones en el sistema.
                </p>
              </div>
            )}

            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-torta-cream/50 rounded-xl">
                <span className="text-torta-muted">Comisión Base</span>
                <span className="font-medium text-torta-dark">
                  {formatCurrency(comisionCalculada.comision_base)}
                </span>
              </div>
              {comisionCalculada.bono_escala_diaria > 0 && (
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-xl">
                  <span className="text-green-700">Bono Escala Diaria</span>
                  <span className="font-medium text-green-800">
                    +{formatCurrency(comisionCalculada.bono_escala_diaria)}
                  </span>
                </div>
              )}
              {comisionCalculada.bono_portafolio > 0 && (
                <div className="flex justify-between items-center p-3 bg-blue-50 rounded-xl">
                  <span className="text-blue-700">Bono Portafolio</span>
                  <span className="font-medium text-blue-800">
                    +{formatCurrency(comisionCalculada.bono_portafolio)}
                  </span>
                </div>
              )}
              {comisionCalculada.bono_zona > 0 && (
                <div className="flex justify-between items-center p-3 bg-purple-50 rounded-xl">
                  <span className="text-purple-700">Bono de Zona</span>
                  <span className="font-medium text-purple-800">
                    +{formatCurrency(comisionCalculada.bono_zona)}
                  </span>
                </div>
              )}
            </div>

            {comisionCalculada.desglose && comisionCalculada.desglose.length > 0 && (
              <details className="mt-6">
                <summary className="cursor-pointer text-torta-brown font-medium">
                  Ver desglose detallado
                </summary>
                <div className="mt-4 space-y-2 max-h-60 overflow-y-auto">
                  {comisionCalculada.desglose.map((item, i) => (
                    <div
                      key={i}
                      className="flex justify-between items-center p-2 bg-gray-50 rounded-lg text-sm"
                    >
                      <span className="text-gray-600">
                        {item.tipo === "base"
                          ? `${item.cantidad}x ${item.producto}`
                          : item.tipo === "escala_diaria"
                          ? `Escala diaria (${item.unidades} und)`
                          : item.tipo === "portafolio"
                          ? `Portafolio: ${item.producto}`
                          : `Zona: ${item.zona}`}
                      </span>
                      <span className="font-medium">{formatCurrency(item.subtotal)}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}

        <Button
          onClick={() => navigate("/vendedor")}
          className="w-full h-12 bg-torta-brown hover:bg-torta-brown/90"
        >
          Volver al Inicio
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vendedor-cierre">
      <div>
        <h1 className="font-display text-3xl text-torta-dark">Cierre de Caja</h1>
        <p className="text-torta-muted">Resumen y cierre del día</p>
      </div>

      {/* Fecha y Hora del Cierre */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-5">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
              <Calendar className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-amber-800 font-medium capitalize">{fechaFormateada}</p>
              <p className="text-amber-600 text-sm">Fecha del cierre</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
              <Clock className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-amber-800 font-medium text-2xl">{horaFormateada}</p>
              <p className="text-amber-600 text-sm">Hora actual</p>
            </div>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-torta-brown rounded-2xl p-6 text-white">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
            <Wallet className="w-7 h-7" />
          </div>
          <div>
            <p className="text-white/80 text-sm">Total Ventas del Día</p>
            <p className="font-display text-2xl">{formatCurrency(ventasHoy?.total)}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/20">
          <div>
            <p className="text-white/60 text-xs">Transacciones</p>
            <p className="text-lg font-medium">{ventasHoy?.cantidad_ventas || 0}</p>
          </div>
          <div>
            <p className="text-white/60 text-xs">Unidades vendidas</p>
            <p className="text-lg font-medium">{ventasHoy?.unidades || 0}</p>
          </div>
        </div>
      </div>

      {/* Comisión Estimada */}
      {comisionesInfo && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <TrendingUp className="w-6 h-6 text-green-600" />
            <h3 className="font-medium text-green-800">Comisión Estimada del Día</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white/60 rounded-xl p-3">
              <p className="text-xs text-green-600">Base</p>
              <p className="font-display text-lg text-green-800">
                {formatCurrency(comisionesInfo.hoy?.comision_base)}
              </p>
            </div>
            <div className="bg-white/60 rounded-xl p-3">
              <p className="text-xs text-green-600">Escala</p>
              <p className="font-display text-lg text-green-800">
                {formatCurrency(comisionesInfo.hoy?.bono_escala)}
              </p>
            </div>
            <div className="bg-white/60 rounded-xl p-3">
              <p className="text-xs text-green-600">Portafolio</p>
              <p className="font-display text-lg text-green-800">
                {formatCurrency(comisionesInfo.hoy?.bono_portafolio)}
              </p>
            </div>
            <div className="bg-green-600 rounded-xl p-3 text-white">
              <p className="text-xs text-white/80">Total</p>
              <p className="font-display text-lg">
                {formatCurrency(comisionesInfo.hoy?.total_comision)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 shadow-sm space-y-5">
        <h2 className="font-display text-xl text-torta-dark">Reportar Cierre</h2>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-green-600" />
              Efectivo recibido
            </Label>
            <Input
              type="number"
              required
              value={form.total_efectivo}
              onChange={(e) => setForm({ ...form, total_efectivo: e.target.value })}
              placeholder="0"
              className="h-12"
            />
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-blue-600" />
              Transferencias
            </Label>
            <Input
              type="number"
              required
              value={form.total_transferencia}
              onChange={(e) => setForm({ ...form, total_transferencia: e.target.value })}
              placeholder="0"
              className="h-12"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <RotateCcw className="w-4 h-4 text-amber-600" />
            Unidades devueltas
          </Label>
          <Input
            type="number"
            min="0"
            value={form.unidades_devueltas}
            onChange={(e) => setForm({ ...form, unidades_devueltas: e.target.value })}
            placeholder="0"
            className="h-12"
          />
        </div>

        <div className="space-y-2">
          <Label>Notas u observaciones</Label>
          <Textarea
            value={form.notas}
            onChange={(e) => setForm({ ...form, notas: e.target.value })}
            placeholder="Novedades del día, productos dañados, etc..."
            rows={3}
          />
        </div>

        {/* Fotos de Mermas/Novedades */}
        <div className="space-y-3">
          <Label className="flex items-center gap-2">
            <Camera className="w-4 h-4 text-torta-brown" />
            Fotos de Mermas/Novedades (opcional)
          </Label>
          <p className="text-xs text-torta-muted">
            Adjunta fotos de productos dañados, mermas o novedades del día
          </p>

          {/* Hidden inputs for file/camera */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* Buttons */}
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingFoto}
              className="flex-1"
            >
              <Image className="w-4 h-4 mr-2" />
              Subir Fotos
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => cameraInputRef.current?.click()}
              disabled={uploadingFoto}
              className="flex-1"
            >
              <Camera className="w-4 h-4 mr-2" />
              Tomar Foto
            </Button>
          </div>

          {/* Preview de fotos */}
          {fotosMermas.length > 0 && (
            <div className="grid grid-cols-3 gap-3 mt-3">
              {fotosMermas.map((foto, index) => (
                <div key={index} className="relative group">
                  <img
                    src={foto.data}
                    alt={`Merma ${index + 1}`}
                    className="w-full h-24 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveFoto(index)}
                    className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="h-24 border-2 border-dashed border-torta-sand rounded-lg flex items-center justify-center text-torta-muted hover:border-torta-brown hover:text-torta-brown transition-colors"
              >
                <Plus className="w-6 h-6" />
              </button>
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="bg-torta-cream rounded-xl p-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-torta-muted">Total reportado:</span>
            <span className="font-medium text-torta-dark">{formatCurrency(totalReportado)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-torta-muted">Total sistema:</span>
            <span className="font-medium text-torta-dark">{formatCurrency(ventasHoy?.total)}</span>
          </div>
          <div className="flex justify-between text-sm pt-2 border-t border-white">
            <span className="text-torta-muted">Diferencia:</span>
            <span
              className={`font-medium ${
                diferencia === 0
                  ? "text-green-600"
                  : diferencia > 0
                  ? "text-blue-600"
                  : "text-red-600"
              }`}
            >
              {diferencia === 0 ? "✓ Cuadrado" : formatCurrency(diferencia)}
            </span>
          </div>
        </div>

        {/* Resumen Pre-Envío */}
        {showResumen && (
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-5 space-y-3">
            <h3 className="font-display text-lg text-blue-800 text-center mb-4">
              Confirma tu cierre antes de enviar
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Efectivo reportado:</span>
                <span className="font-medium">{formatCurrency(parseFloat(form.total_efectivo) || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Transferencias:</span>
                <span className="font-medium">{formatCurrency(parseFloat(form.total_transferencia) || 0)}</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-gray-600">Total reportado:</span>
                <span className="font-bold">{formatCurrency(totalReportado)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total sistema:</span>
                <span className="font-medium">{formatCurrency(ventasHoy?.total)}</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-gray-600">Diferencia:</span>
                <span className={`font-bold ${
                  diferencia === 0 ? "text-green-600" : diferencia > 0 ? "text-blue-600" : "text-red-600"
                }`}>
                  {diferencia === 0 ? "✓ Cuadrado" : formatCurrency(diferencia)}
                </span>
              </div>
              {parseInt(form.unidades_devueltas) > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Unidades devueltas:</span>
                  <span className="font-medium text-amber-600">{form.unidades_devueltas}</span>
                </div>
              )}
              {fotosMermas.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Fotos de mermas:</span>
                  <span className="font-medium">{fotosMermas.length} foto(s)</span>
                </div>
              )}
              {form.notas && (
                <div className="border-t pt-2">
                  <span className="text-gray-600">Notas:</span>
                  <p className="text-gray-800 mt-1">{form.notas}</p>
                </div>
              )}
            </div>
            
            <button
              type="button"
              onClick={() => setShowResumen(false)}
              className="text-sm text-blue-600 hover:underline w-full text-center mt-2"
            >
              ← Modificar datos
            </button>
          </div>
        )}

        <Button
          type="submit"
          disabled={submitting}
          className="w-full h-12 bg-torta-brown hover:bg-torta-brown/90"
        >
          {submitting ? (
            <span className="flex items-center gap-2">
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Enviando...
            </span>
          ) : showResumen ? (
            <span className="flex items-center gap-2">
              <Send className="w-5 h-5" />
              Confirmar y Enviar Cierre
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Send className="w-5 h-5" />
              Revisar antes de enviar
            </span>
          )}
        </Button>
      </form>
    </div>
  );
}
